/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { useSteps, Step, type StepState } from '../hooks/use-steps';
import type { ResourceApproval } from '@/data/subsidy/interfaces';
import { useResourceApprovalShow } from '@/data/subsidy/resource-approvals';
import type { RefundStatus, StepStatus } from '@/models/subsidy/status';

interface RefundRequestContextValue {
  resourceApproval: ResourceApproval | null;
  refundStatus: RefundStatus | null;
  isLoading: boolean;
  currentStep: Step;
  stepStatus: StepStatus | null;
  getStepState: (step: Step) => StepState;
}

const RefundRequestContext = createContext<RefundRequestContextValue | null>(
  null
);

export const RefundRequestProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { id, refund_request_uuid } = useParams<{
    id: string;
    refund_request_uuid?: string;
  }>();

  const { resourceApproval, isLoading } = useResourceApprovalShow(
    id!,
    refund_request_uuid ?? ''
  );

  const refundStatus = resourceApproval?.status || null;

  const { currentStep, stepStatus, getStepState } = useSteps(resourceApproval);

  return (
    <RefundRequestContext.Provider
      value={{
        resourceApproval,
        refundStatus,
        isLoading,
        currentStep,
        stepStatus,
        getStepState,
      }}
    >
      {children}
    </RefundRequestContext.Provider>
  );
};

export const useRefundRequest = () => {
  const context = useContext(RefundRequestContext);
  if (!context) {
    throw new Error(
      'useRefundRequest must be used within RefundRequestProvider'
    );
  }
  return context;
};
